import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-hot-toast';
import type { StarAvailabilitySettings } from '../types/availability';

interface SettingsFormProps {
  settings: StarAvailabilitySettings;
  userId: string;
  onUpdate: (settings: StarAvailabilitySettings) => void;
}

const SettingsForm: React.FC<SettingsFormProps> = ({ settings, userId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState(settings);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await updateDoc(doc(db, 'users', userId), {
        minCallDuration: formData.minCallDuration,
        maxCallDuration: formData.maxCallDuration,
        pricePerMinute: formData.pricePerMinute,
        allowBidding: formData.allowBidding !== undefined ? formData.allowBidding : true,
      });
      onUpdate(formData);
      toast.success('Settings saved successfully!');
    } catch (err) {
      setError('Failed to update settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label htmlFor="minCallDuration" className="block text-sm font-medium text-gray-700 mb-1">
            Minimum Call Duration (minutes)
          </label>
          <input
            type="number"
            min="1"
            value={formData.minCallDuration ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((prev: any) => ({
                ...prev,
                minCallDuration: val === '' ? '' : parseInt(val)
              }));
            }}
            onBlur={(e) => {
              if (e.target.value === '' || isNaN(Number(e.target.value))) {
                setFormData((prev: any) => ({ ...prev, minCallDuration: 1 }));
              }
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            placeholder="e.g., 10"
          />
        </div>

        <div>
          <label htmlFor="maxCallDuration" className="block text-sm font-medium text-gray-700 mb-1">
            Maximum Call Duration (minutes)
          </label>
          <input
            type="number"
            min={formData.minCallDuration || 1}
            value={formData.maxCallDuration ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((prev: any) => ({
                ...prev,
                maxCallDuration: val === '' ? '' : parseInt(val)
              }));
            }}
            onBlur={(e) => {
              if (e.target.value === '' || isNaN(Number(e.target.value))) {
                setFormData((prev: any) => ({ ...prev, maxCallDuration: prev.minCallDuration || 1 }));
              }
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            placeholder="e.g., 30"
          />
        </div>

        <div>
          <label htmlFor="pricePerMinute" className="block text-sm font-medium text-gray-700 mb-1">
            Price per Minute ($)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.pricePerMinute ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((prev: any) => ({
                ...prev,
                pricePerMinute: val === '' ? '' : parseFloat(val)
              }));
            }}
            onBlur={(e) => {
              if (e.target.value === '' || isNaN(Number(e.target.value))) {
                setFormData((prev: any) => ({ ...prev, pricePerMinute: 0 }));
              }
            }}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            placeholder="e.g., 1.00"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <input
          type="checkbox"
          id="allowBidding"
          checked={formData.allowBidding !== undefined ? formData.allowBidding : true}
          onChange={(e) => setFormData((prev: any) => ({
            ...prev,
            allowBidding: e.target.checked
          }))}
          className="w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
        />
        <label htmlFor="allowBidding" className="text-sm font-medium text-gray-700 cursor-pointer">
          Allow fans to bid on overlapping time slots (enables competitive booking for popular times)
        </label>
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-4 rounded-md text-sm shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
      >
        {loading ? 'Saving...' : 'Save Settings'}
      </button>
    </form>
  );
};

export default SettingsForm;